import { ArrowLeft, Wifi, Users, UserPlus, LogOut, Trash2, User, Plus, Server, X, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import SimpleLoginDialog from '@/components/auth/SimpleLoginDialog';
import SimpleSignupDialog from '@/components/auth/SimpleSignupDialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNavigate } from 'react-router-dom';
import { useLoggedInAccounts, type Account } from '@/hooks/useLoggedInAccounts';
import { genUserName } from '@/lib/genUserName';
import { useState } from 'react';
import { useAppContext } from '@/hooks/useAppContext';
import { cn } from '@/lib/utils';

export function NostrSettings() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { authors, currentUser, setLogin, removeLogin, isLoading } = useLoggedInAccounts();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showSignupDialog, setShowSignupDialog] = useState(false);
  const { config, updateConfig, presetRelays } = useAppContext();
  const [newServerHostname, setNewServerHostname] = useState('');
  const [newRelayUrl, setNewRelayUrl] = useState('');

  const handleSwitchAccount = (accountId: string) => {
    setLogin(accountId);
  };

  const handleRemoveAccount = (accountId: string) => {
    removeLogin(accountId);
  };

  const getDisplayName = (account: Account) => {
    return account.metadata?.name ?? genUserName(account.pubkey);
  };

  const handleAddServer = () => {
    const hostname = newServerHostname.trim();
    if (!hostname) return;
    if (!/^[a-zA-Z0-9][a-zA-Z0-9-_.]*[a-zA-Z0-9]$/.test(hostname)) return;

    updateConfig((current) => ({
      ...current,
      ngitServers: [...(current.ngitServers ?? config.ngitServers), hostname],
    }));
    setNewServerHostname('');
  };

  const handleRemoveServer = (hostname: string) => {
    updateConfig((current) => ({
      ...current,
      ngitServers: (current.ngitServers ?? config.ngitServers).filter(s => s !== hostname),
    }));
  };

  // Normalize relay URL by adding wss:// if no protocol is present
  const normalizeRelayUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;

    // Check if it already has a protocol
    if (trimmed.includes('://')) {
      return trimmed;
    }

    // Add wss:// prefix
    return `wss://${trimmed}`;
  };

  // Check if input value looks like a valid relay URL
  const isValidRelayInput = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) return false;

    const normalized = normalizeRelayUrl(trimmed);
    try {
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  };

  const handleAddRelay = () => {
    const url = normalizeRelayUrl(newRelayUrl);
    if (!url || !isValidRelayInput(newRelayUrl)) return;

    // Avoid duplicates
    if (config.relayUrls.includes(url)) {
      setNewRelayUrl('');
      return;
    }

    updateConfig((current) => ({
      ...current,
      relayUrls: [...(current.relayUrls ?? config.relayUrls), url],
    }));
    setNewRelayUrl('');
  };

  const handleRemoveRelay = (url: string) => {
    // Keep at least one relay
    if (config.relayUrls.length <= 1) return;

    updateConfig((current) => ({
      ...current,
      relayUrls: (current.relayUrls ?? config.relayUrls).filter(r => r !== url),
    }));
  };

  return (
    <div className="p-6 space-y-8">
      {isMobile && (
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings')}
            className="h-8 px-2 -ml-2 text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('settings')}
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold tracking-tight">{t('nostrSettings')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('nostrSettingsDescriptionLong')}
        </p>
      </div>

      <div className="space-y-6">
        {/* Account Management */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">{t('nostrAccounts')}</h3>
          </div>

          {isLoading ? (
            <div className="border rounded-lg overflow-hidden bg-card">
              <div className="flex items-center gap-3 p-3 border-b">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="flex items-center gap-3 p-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ) : authors.length === 0 ? (
            <div className="text-center py-8 border rounded-lg bg-card">
              <User className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-4">{t('noAccountsLoggedIn')}</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center px-4">
                <Button onClick={() => setShowSignupDialog(true)} size="sm" className="gap-2">
                  <UserPlus className="h-4 w-4" />
                  {t('createAccount')}
                </Button>
                <Button variant="outline" onClick={() => setShowLoginDialog(true)} size="sm" className="gap-2">
                  <LogOut className="h-4 w-4 rotate-180" />
                  {t('addExistingAccount')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden bg-card">
              {authors.map((account) => (
                <div
                  key={account.id}
                  className={cn(
                    "flex items-center gap-3 p-3 transition-colors border-b last:border-b-0",
                    currentUser?.id === account.id
                      ? "bg-primary/5"
                      : "hover:bg-muted/50 cursor-pointer"
                  )}
                  onClick={() => currentUser?.id !== account.id && handleSwitchAccount(account.id)}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={account.metadata?.picture} alt={getDisplayName(account)} />
                    <AvatarFallback>{getDisplayName(account).charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{getDisplayName(account)}</p>
                    {currentUser?.id === account.id && (
                      <p className="text-xs text-muted-foreground">{t('currentAccount')}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); handleRemoveAccount(account.id); }}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <button
                className="flex items-center gap-3 p-3 w-full hover:bg-muted/50 transition-colors"
                onClick={() => setShowLoginDialog(true)}
              >
                <div className="h-10 w-10 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                  <Plus className="h-4 w-4 text-muted-foreground" />
                </div>
                <span className="text-sm text-muted-foreground">{t('addAccount')}</span>
              </button>
            </div>
          )}
        </div>

        {/* Relay Configuration */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Wifi className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">{t('relayConfiguration')}</h3>
          </div>
          <div className="space-y-2">
            {config.relayUrls.map((url) => {
              const presetRelay = presetRelays?.find(r => r.url === url);
              const displayName = url.replace(/^wss?:\/\//, '').replace(/\/$/, '');

              return (
                <div
                  key={url}
                  className="flex items-center gap-2 p-2.5 rounded-lg border bg-card"
                >
                  <Wifi className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="font-mono text-sm flex-1 truncate">{displayName}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveRelay(url)}
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    disabled={config.relayUrls.length <= 1}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder={t('enterRelayUrl')}
              value={newRelayUrl}
              onChange={(e) => setNewRelayUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddRelay()}
              className="h-9"
            />
            <Button
              onClick={handleAddRelay}
              disabled={!newRelayUrl.trim() || !isValidRelayInput(newRelayUrl)}
              variant="outline"
              size="sm"
              className="h-9 shrink-0"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t('addRelay')}
            </Button>
          </div>
        </div>

        {/* Nostr Git Servers */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">{t('nostrGitServers')}</h3>
          </div>
          <div className="space-y-2">
            {config.ngitServers.map((hostname) => (
              <div
                key={hostname}
                className="flex items-center gap-2 p-2.5 rounded-lg border bg-card"
              >
                <Server className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-mono text-sm flex-1 truncate">{hostname}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveServer(hostname)}
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  disabled={config.ngitServers.length <= 1}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder={t('enterHostname')}
              value={newServerHostname}
              onChange={(e) => setNewServerHostname(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddServer()}
              className="h-9"
            />
            <Button onClick={handleAddServer} disabled={!newServerHostname.trim()} variant="outline" size="sm" className="h-9 shrink-0">
              <Plus className="h-4 w-4 mr-1" />
              {t('addServer')}
            </Button>
          </div>
        </div>
      </div>

      <SimpleLoginDialog
        isOpen={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onLogin={() => setShowLoginDialog(false)}
        onSignup={() => { setShowLoginDialog(false); setShowSignupDialog(true); }}
      />
      <SimpleSignupDialog
        isOpen={showSignupDialog}
        onClose={() => setShowSignupDialog(false)}
        onComplete={() => setShowSignupDialog(false)}
        onLogin={() => { setShowSignupDialog(false); setShowLoginDialog(true); }}
      />
    </div>
  );
}

export default NostrSettings;
